// [COMPOSANT TUTORIEL] Auto-start et gestion manuelle des tutoriels
// [RÔLE] Détecter première connexion/navigation et gérer les tutoriels automatiques + manuels
// [ARCHITECTURE] React component avec useFirstLogin + useTutorial + événements globaux

import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useFirstLogin } from '../../hooks/useFirstLogin';
import { useTutorial } from '../../hooks/useTutorial';
import { homeTutorialSteps, processCreateTutorialSteps } from '../../config/tutorialSteps';
import { useTranslation } from 'react-i18next';

const FirstLoginTutorial: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isFirstLogin, isLoading, hasChecked } = useFirstLogin();
  
  // [TUTORIEL HOME] Hook pour la page d'accueil
  const {
    startTutorial: startHomeTutorial,
    run: runHome,
    isTutorialCompleted: isHomeTutorialCompleted
  } = useTutorial(homeTutorialSteps(t), 'home', false);

  // [TUTORIEL PROCESS CREATE] Hook pour la création de processus
  const {
    startTutorial: startProcessTutorial,
    run: runProcess,
    isTutorialCompleted: isProcessTutorialCompleted
  } = useTutorial(processCreateTutorialSteps(t), 'processCreate', false);

  useEffect(() => {
    // [GUARD] Attendre que la vérification soit terminée
    if (isLoading || !hasChecked) return;
    
    // [GUARD] Tutoriels déjà en cours - EMPÊCHE LE REDÉMARRAGE
    if (runHome || runProcess) {
      // console.log('🛑 Tutoriel déjà en cours, auto-start bloqué');
      return;
    }
    
    // [CONDITION AUTO-START] UNIQUEMENT lors de la première connexion
    const isHomePage = location.pathname === '/' || location.pathname === '/home';
    const autoStartKey = `tutorial_${isHomePage ? 'home' : 'processCreate'}_auto_started`;
    const alreadyAutoStarted = localStorage.getItem(autoStartKey) === 'true';
    
    // [IMPORTANT] Ne déclencher QUE si is_first_login=true ET pas déjà auto-démarré
    const shouldAutoStart = isFirstLogin && !alreadyAutoStarted;

    if (!shouldAutoStart) {
      // console.log('🚫 Auto-start bloqué:', { isFirstLogin, hasChecked, alreadyAutoStarted, isHomePage });
      return;
    }

    // [AUTO-START HOME] Page d'accueil
    if (isHomePage) {
      // [GUARD] Tutoriel home déjà complété
      if (isHomeTutorialCompleted()) return;
      
      const timer = setTimeout(() => {
        // [VÉRIFICATION ÉLÉMENTS HOME] S'assurer que les éléments cibles existent
        const discoverElement = document.querySelector('[data-tutorial="discover-process"]');
        const generateElement = document.querySelector('[data-tutorial="generate-diagram"]');
        
        if (discoverElement && generateElement) {
          // console.log('🎯 Auto-start tutoriel HOME (première connexion ou navigation)');
          
          // [MARQUER COMME AUTO-DÉMARRÉ] AVANT de démarrer pour éviter les doubles appels
          localStorage.setItem('tutorial_home_auto_started', 'true');
          
          startHomeTutorial();
        } else {
          console.warn('⚠️ Éléments tutoriel HOME non trouvés, retry...');
          // [MARQUER COMME AUTO-DÉMARRÉ] même en cas d'échec pour éviter les retry infinis
          localStorage.setItem('tutorial_home_auto_started', 'true');
          
          setTimeout(() => {
            const retryDiscover = document.querySelector('[data-tutorial="discover-process"]');
            const retryGenerate = document.querySelector('[data-tutorial="generate-diagram"]');
            
            if (retryDiscover && retryGenerate) {
              // console.log('🎯 Retry réussi - Démarrage tutoriel HOME');
              startHomeTutorial();
            } else {
              console.warn('🚫 Retry échoué - Éléments toujours introuvables');
            }
          }, 2000);
        }
      }, 1500);

      return () => clearTimeout(timer);
    }
    
    // [AUTO-START PROCESS CREATE] Page création de processus
    if (location.pathname === '/process/create') {
      // [GUARD] Tutoriel process déjà complété
      if (isProcessTutorialCompleted()) return;
      
      const timer = setTimeout(() => {
        // [VÉRIFICATION ÉLÉMENTS PROCESS] S'assurer que les éléments cibles existent
        const processElement = document.querySelector('.tutorial-process-create-welcome');
        
        if (processElement) {
          console.log('🎯 Auto-start tutoriel PROCESS CREATE (première connexion ou navigation)');
          
          // [MARQUER COMME AUTO-DÉMARRÉ] AVANT de démarrer pour éviter les doubles appels
          localStorage.setItem('tutorial_processCreate_auto_started', 'true');
          
          startProcessTutorial();
        } else {
          console.warn('⚠️ Éléments tutoriel PROCESS CREATE non trouvés, retry...');
          // [MARQUER COMME AUTO-DÉMARRÉ] même en cas d'échec pour éviter les retry infinis
          localStorage.setItem('tutorial_processCreate_auto_started', 'true');
          
          setTimeout(() => {
            const retryProcess = document.querySelector('.tutorial-process-create-welcome');
            
            if (retryProcess) {
              console.log('🎯 Retry réussi - Démarrage tutoriel PROCESS CREATE');
              startProcessTutorial();
            } else {
              console.warn('🚫 Retry échoué - Élément toujours introuvable');
            }
          }, 2000);
        }
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [
    isFirstLogin, 
    isLoading, 
    hasChecked, 
    location.pathname, 
    startHomeTutorial,
    startProcessTutorial,
    runHome,
    runProcess,
    isHomeTutorialCompleted,
    isProcessTutorialCompleted
  ]);

  // [RENDER] Composant logique + debug
  return (
    <>
    
       {/* {process.env.NODE_ENV === 'development' && hasChecked && (
        <div 
          style={{
            position: 'fixed',
            bottom: '10px',
            left: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '10px',
            borderRadius: '6px',
            fontSize: '11px',
            zIndex: 9999,
            maxWidth: '280px',
            fontFamily: 'monospace',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '6px', color: '#00ff88' }}>
            🎯 First Login Debug
          </div>
          <div>Is First: <span style={{ color: isFirstLogin ? '#00ff88' : '#ff6b6b' }}>
            {isFirstLogin ? 'Yes' : 'No'}
          </span></div>
          <div>Loading: <span style={{ color: isLoading ? '#ffd93d' : '#00ff88' }}>
            {isLoading ? 'Yes' : 'No'}
          </span></div>
          <div>Page: <span style={{ color: '#6bcfff' }}>{location.pathname}</span></div>
          <div>Tutorial Running: <span style={{ color: (runHome || runProcess || runSampleProcess) ? '#00ff88' : '#ff6b6b' }}>
            {(runHome || runProcess || runSampleProcess) ? 'Yes' : 'No'}
          </span></div>
          <div style={{ marginTop: '6px', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '6px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px', color: getBrandingColor() }}>
              📦 Sample Process
            </div>
            <div>Has Sample: <span style={{ color: hasSampleProcess ? '#00ff88' : '#ff6b6b' }}>
              {hasSampleProcess ? 'Yes' : 'No'}
            </span></div>
            <div>Sample Checked: <span style={{ color: sampleChecked ? '#00ff88' : '#ff6b6b' }}>
              {sampleChecked ? 'Yes' : 'No'}
            </span></div>
            <div>Home Completed: <span style={{ color: localStorage.getItem('tutorial_home_completed') === 'true' ? '#00ff88' : '#ff6b6b' }}>
              {localStorage.getItem('tutorial_home_completed') === 'true' ? 'Yes' : 'No'}
            </span></div>
            <div>Sample Auto Started: <span style={{ color: localStorage.getItem('tutorial_sampleProcess_auto_started') === 'true' ? '#00ff88' : '#ff6b6b' }}>
              {localStorage.getItem('tutorial_sampleProcess_auto_started') === 'true' ? 'Yes' : 'No'}
            </span></div>
            <div>Process ID: <span style={{ color: '#6bcfff', fontSize: '9px' }}>
              {sampleProcessId ? sampleProcessId.substring(0, 8) + '...' : 'null'}
            </span></div>
          </div>
          <div style={{ marginTop: '6px', fontSize: '10px', opacity: 0.7 }}>
            localStorage: {localStorage.getItem('famla_first_login_done') || 'null'}
          </div>
          <div style={{ fontSize: '9px', opacity: 0.6, marginTop: '4px' }}>
            Token: {users.access_token ? 'Present' : 'Missing'}
          </div>
        </div>
      )}
      
       {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'fixed',
          bottom: '10px',
          right: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          zIndex: 9998
        }}>
          <button
            onClick={() => {
              localStorage.removeItem('famla_first_login_done');
              localStorage.removeItem('tutorial_home_completed');
              localStorage.removeItem('tutorial_home_auto_started');
              localStorage.removeItem('tutorial_sampleProcess_completed');
              localStorage.removeItem('tutorial_sampleProcess_auto_started');
              window.location.reload();
            }}
            style={{
              padding: '8px 12px',
              backgroundColor: getBrandingColor(),
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            🔄 Reset First Login
          </button>
          
          <button
            onClick={() => {
              localStorage.removeItem('tutorial_sampleProcess_auto_started');
              localStorage.removeItem('tutorial_sampleProcess_completed');
              console.log('🔄 Reset sample tutorial flags');
            }}
            style={{
              padding: '8px 12px',
              backgroundColor: '#ffd93d',
              color: 'black',
              border: 'none',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            🔄 Reset Sample Tutorial
          </button>
          
          <button
            onClick={startHomeTutorial}
            style={{
              padding: '8px 12px',
              backgroundColor: '#00ff88',
              color: 'black',
              border: 'none',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            🎯 Start Home Tutorial
          </button>
          
          <button
            onClick={startSampleProcessTutorial}
            style={{
              padding: '8px 12px',
              backgroundColor: '#6bcfff',
              color: 'black',
              border: 'none',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            📦 Start Sample Tutorial
          </button>
          
        </div>
      )} */}
     </>
  );
};

export default FirstLoginTutorial;
